import React, { Component } from 'react';
import CartPageItem from '../../components/CartPageItem/CartPageItem';
import { CartPageItemType } from '../../components/CartPageItem/CartPageItem.props';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import ResultPrice from '../../components/ResultPrice/ResultPrice';
import Section66to33 from '../../components/Section66to33/Section66to33';
import { GlobalContext } from '../../interfaces/GlobalContext/GlobalContext';
import ProductPrice from '../../interfaces/ProductPrice/ProductPrice';
import ProductProperties, { FormFieldName } from '../../interfaces/ProductProperties/ProductProperties';
import { ProductType } from '../../interfaces/ProductProperties/ProductPropertiesRelated';
import CartPageProps from './CartPage.props';
import CartPageState from './CartPage.state';

export default class CartPage extends Component<CartPageProps, CartPageState> {
    static contextType = GlobalContext;
    context!: React.ContextType<typeof GlobalContext>;
    private modalElement: React.RefObject<HTMLDivElement>;

    constructor(props: CartPageProps) {
        super(props);
        this.state = {
            prices: {},
            isDirectPurchaseMode: false
        };

        this.modalElement = React.createRef<HTMLDivElement>();
        this.refreshPage = this.refreshPage.bind(this);
        this.updatePrices = this.updatePrices.bind(this);
        this.checkData = this.checkData.bind(this);
        this.getCartData = this.getCartData.bind(this);
        this.clearDirectPurchaseData = this.clearDirectPurchaseData.bind(this);
        this.clearStalePrices = this.clearStalePrices.bind(this);
    }

    refreshPage() {
        // Clear stale prices when refreshing the page (e.g., after cart changes)
        this.clearStalePrices();
        this.forceUpdate();
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.checkData();
        
        // Set up cleanup listeners for direct purchase
        const { isDirectPurchase } = this.getCartData();
        if (isDirectPurchase) {
            this.setupDirectPurchaseCleanup();
            // Clear any stale prices when entering direct purchase mode
            this.clearStalePrices();
        }
        
        // Set initial cart mode in state
        this.setState({
            isDirectPurchaseMode: isDirectPurchase
        });
    }

    componentDidUpdate() {
        // Check if cart mode has changed
        const { isDirectPurchase } = this.getCartData();
        if (this.state.isDirectPurchaseMode !== isDirectPurchase) {
            // Mode changed, clear prices and update state
            this.setState({
                prices: {},
                isDirectPurchaseMode: isDirectPurchase
            });
        }
    }

    componentWillUnmount() {
        // Clean up event listeners
        window.removeEventListener('beforeunload', this.clearDirectPurchaseData);
    }

    setupDirectPurchaseCleanup() {
        // Clear on page unload
        window.addEventListener('beforeunload', this.clearDirectPurchaseData);
    }

    clearDirectPurchaseData() {
        this.context.sessionStorageProvider.clearDirectPurchaseFromSessionStorage();
    }

    clearStalePrices() {
        // Clear all prices when switching cart modes to prevent stale price data
        this.setState({
            prices: {}
        });
    }

    // Method to determine cart data source
    getCartData(): { warenkorbData: ProductProperties[], isDirectPurchase: boolean, campaignId?: string } {
        // Check for direct purchase first
        const directPurchaseData = this.context.sessionStorageProvider.getDirectPurchaseFromSessionStorage();
        if (directPurchaseData) {
            return {
                warenkorbData: [directPurchaseData.productProperties],
                isDirectPurchase: true,
                campaignId: directPurchaseData.campaignId
            };
        }
        
        // Fall back to regular cart
        const regularCart = this.context.localStorageProvider.getProductInformationFromLocalStorage();
        return {
            warenkorbData: regularCart?.warenkorbData || [],
            isDirectPurchase: false,
            campaignId: undefined
        };
    }

    updatePrices(pProductPrice: ProductPrice) {
        const prices = this.state.prices;
        
        if (pProductPrice.price === 0) {
            delete prices[pProductPrice.magicNumber as string];
        } else {
            prices[pProductPrice.magicNumber as string] = pProductPrice;
        }

        this.setState({
            prices: prices
        });
    }

    async checkData() {
        const userInformation = await this.context.loginProvider.getUserInformation();
        const modal = document.querySelector('.modal');

        if (userInformation === 403) {
            // this.modalElement.current?.instance.show();
            modal?.classList.add('modal--visible');
            this.setState({
                modal: 'noPermission',
                userPermission: false,
            })
        } else if (userInformation === 500) {
            modal?.classList.add('modal--visible');
            this.setState({
                modal: 'severError',
                userPermission: false,
            })
        } else if (userInformation === 401) {
            modal?.classList.add('modal--visible');
            this.setState({
                modal: 'expired',
                userPermission: false,
            })
        } else {
            this.setState({
                userPermission: true,
            })
        }
    }

    handleBackClick() {
        const modal = document.querySelector('.modal');
        modal?.classList.remove('modal--visible');
    }

    render(): React.ReactNode {
        const { warenkorbData, isDirectPurchase } = this.getCartData();
        
        return (
            <>
                <ProgressBar steps={(() => {
                    const steps = this.context.i18nProvider.get(
                        isDirectPurchase ? 'progressBar.directPurchase.steps' : 'progressBar.steps', 
                        { returnObjects: true }
                    ) as unknown as Array<string>;
                    return Array.isArray(steps) ? steps : (this.context.i18nProvider.get('progressBar.steps', { returnObjects: true }) as unknown as Array<string> || []);
                })()} 
                activeStep={0} 
                noSticky={true} />
                
                {warenkorbData.length !== 0 ? <>
                    <section className="cs-cart-page content-section">
                        <div className="container">
                            <div className="cart-page cart-page--reduce-spaces">
                                <div className="cart-page__container">
                                    <h1 className="cart-page__title">{
                                        this.context.i18nProvider.get(
                                            isDirectPurchase ? 'cartPage.directPurchase.title' : 'cartPage.title'
                                        ) || this.context.i18nProvider.get('cartPage.title') || ''
                                    }</h1>
                                    <ul className="cart-page__products">
                                        {warenkorbData.map(
                                            (productProperties: ProductProperties) => {
                                                return <CartPageItem productProperties={productProperties}
                                                    key={productProperties.magicNumber}
                                                    updateHandler={this.refreshPage}
                                                    updatePrices={this.updatePrices}
                                                    cartPage={true}
                                                    isDirectPurchase={isDirectPurchase}
                                                    license={productProperties.configOptions?.find(productOption => productOption.name === FormFieldName.NUMBEROFLICENCES)?.value}
                                                    variant={productProperties.title}
                                                    type={
                                                        productProperties.productType.toLocaleLowerCase() === CartPageItemType.TEST
                                                            ? CartPageItemType.TEST
                                                            : CartPageItemType.PRODUCT
                                                    } />
                                            }
                                        )}
                                        {
                                            warenkorbData.length && !isDirectPurchase ? <>
                                                <CartPageItem
                                                    productProperties={{
                                                        configOptions: [],
                                                        magicNumber: "",
                                                        productType: ProductType.BASE,
                                                        professionalContract: false,
                                                        relatedProducts: [],
                                                        queryInformation: {
                                                            imageSrc: this.context.i18nProvider.get('cartpageServiceItem.imageSrc') || "",
                                                            imageAlt: this.context.i18nProvider.get('cartpageServiceItem.imageAlt') || "",
                                                            title: this.context.i18nProvider.get('cartpageServiceItem.title') || "",
                                                            href: undefined,
                                                        }
                                                    }}
                                                    type={CartPageItemType.SERVICE}
                                                    key={'servicepaket'} />
                                            </> : <></>
                                        }
                                    </ul>
                                </div>
                                <div className="cart-page__modal">
                                    <div className="modal" ref={this.modalElement}>
                                        <div className="modal__container container">
                                            <div className="modal__content">
                                                <button className="modal__close-btn" type="button" onClick={this.handleBackClick}>
                                                    <span className="modal__close-btn-icon"></span>
                                                </button>
                                                <div className="modal__body">
                                                    <div className="textblock" dangerouslySetInnerHTML={{
                                                        __html: this.state.modal === 'noPermission'
                                                            ? this.context.i18nProvider.get('customerDataPage.loginSection.noOrderPossible') || ''
                                                            : (this.state.modal === 'expired' ? this.context.i18nProvider.get('customerDataPage.loginSection.tokenExpired') || '' 
                                                                : (this.state.modal === 'serverError' ? this.context.i18nProvider.get('customerDataPage.loginSection.serverError') || '' : ''))
                                                    }}></div>
                                                    <br/>
                                                    <div className="result-price__back-button-wrapper">
                                                        <button className="text-icon-link text-icon-link--button text-icon-link--back" 
                                                            type='button' 
                                                            onClick={this.handleBackClick}>
                                                            <span className="text-icon-link__icon"></span>
                                                            <span className="text-icon-link__text">{this.context.i18nProvider.get ? this.context.i18nProvider.get('cartPage.totalPriceComponent.buttons.back') : null}</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <ResultPrice prices={{...this.state.prices}} userPermission={this.state.userPermission} />
                </> : <>
                    <Section66to33 content100={this.context.i18nProvider.get('emptyCart.header')} content33={this.context.i18nProvider.get('emptyCart.contentInfo')} content66={this.context.i18nProvider.get('emptyCart.help')} />
                </>}
            </>
        );
    }
}
